<template>
  <div class="container-fluid">
    <section>
      <div class="py-8">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="col-12 col-lg-auto mb-4 mb-lg-0">
            <div class="d-flex align-items-center">
              <h4 class="mb-0 me-2">{{ $t('purchase_insights') }}</h4>
              <button 
                class="btn btn-sm btn-outline-secondary ms-2" 
                @click="showSettings = !showSettings"
                :title="$t('customize_dashboard')"
              >
                <AppIcon name="bi-gear" class="bi bi-gear" />
              </button>
            </div>
          </div>
          <div class="col-12 col-lg-3">
            <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select">
              <option value="">{{$t('filter_by_date')}}</option>
              <option value="today">{{$t('today')}}</option>
              <option value="yesterday">{{$t('yesterday')}}</option>
              <option value="this_week">{{$t('this_week')}}</option>
              <option value="last_week">{{$t('last_week')}}</option>
              <option value="this_month">{{$t('this_month')}}</option>
              <option value="custom">{{$t('custom_date')}}</option>
            </select>
          </div>
          <div class="col-12 col-lg-3" v-if="dateRangeOption === 'custom'">
            <date-picker
              :clearable="true"
              :editable="false"
              class="w-100"
              v-model="range"
              type="date"
              range
              :multi-calendars="1"
              :placeholder="$t('select_date_range')"
            ></date-picker>
          </div>
        </div>
        
        <!-- Settings Panel -->
        <div v-if="showSettings" class="card mt-3 mb-3 shadow-sm">
          <div class="card-body">
            <h6 class="card-title mb-3"><AppIcon name="bi-sliders" class="bi bi-sliders me-2" />{{$t('customize_dashboard')}}</h6>
            <div class="row">
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.totalPurchases" id="togglePurchases">
                  <label class="form-check-label small" for="togglePurchases">{{$t('total_purchases')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.totalCost" id="toggleCost">
                  <label class="form-check-label small" for="toggleCost">{{$t('total_cost')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.potentialProfit" id="toggleProfit">
                  <label class="form-check-label small" for="toggleProfit">{{$t('potential_profit')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.avgPurchase" id="toggleAvg">
                  <label class="form-check-label small" for="toggleAvg">{{$t('avg_purchase_value')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.statusBreakdown" id="toggleStatus">
                  <label class="form-check-label small" for="toggleStatus">{{$t('purchase_status_breakdown')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.dailyPurchases" id="toggleDaily">
                  <label class="form-check-label small" for="toggleDaily">{{$t('daily_purchase_trend')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.topProducts" id="toggleProducts">
                  <label class="form-check-label small" for="toggleProducts">{{$t('top_purchased_products')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.topSuppliers" id="toggleSuppliers">
                  <label class="form-check-label small" for="toggleSuppliers">{{$t('top_suppliers')}}</label>
                </div>
              </div>
              <div class="col-md-3 col-sm-6 mb-2">
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" v-model="visibleCards.pendingPurchases" id="togglePending">
                  <label class="form-check-label small" for="togglePending">{{$t('pending_purchases')}}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Metrics Cards Row 1 -->
    <div class="row mb-3">
      <!-- Total Purchases -->
      <InsightCard
        :visible="visibleCards.totalPurchases"
        :title="$t('total_purchases')"
        icon="cart-check"
        @refresh="fetchInsights"
      >
        <div v-if="insights.summary">
          <h5 class="fw-bold text-primary mb-0">{{ formatMoney(insights.summary.totalPurchases) }}</h5>
          <small class="text-muted">{{ insights.summary.purchasesCount }} {{$t('purchases')}}</small>
        </div>
        <div v-else class="text-center">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
      
      <!-- Total Cost -->
      <InsightCard
        :visible="visibleCards.totalCost"
        :title="$t('total_cost')"
        icon="cash-stack"
        header-style="background-color: #dc3545 !important; color: white;"
        @refresh="fetchInsights"
      >
        <div v-if="insights.summary">
          <h5 class="fw-bold text-danger mb-0">{{ formatMoney(insights.summary.totalCost) }}</h5>
          <small class="text-muted">{{$t('actual_cost')}}</small>
        </div>
        <div v-else class="text-center">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
      
      <!-- Potential Profit -->
      <InsightCard
        :visible="visibleCards.potentialProfit"
        :title="$t('potential_profit')"
        icon="graph-up-arrow"
        @refresh="fetchInsights"
      >
        <div v-if="insights.summary">
          <h5 class="fw-bold mb-0" :class="insights.summary.potentialProfit >= 0 ? 'text-success' : 'text-danger'">
            {{ formatMoney(insights.summary.potentialProfit) }}
          </h5>
          <small class="text-muted">{{$t('if_sold_at_retail')}}</small>
        </div>
        <div v-else class="text-center">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
      
      <!-- Average Purchase Value -->
      <InsightCard
        :visible="visibleCards.avgPurchase"
        :title="$t('avg_purchase_value')"
        icon="calculator"
        header-style="background-color: #17a2b8 !important; color: white;"
        @refresh="fetchInsights"
      >
        <div v-if="insights.summary">
          <h5 class="fw-bold text-info mb-0">{{ formatMoney(insights.summary.avgPurchaseValue) }}</h5>
          <small class="text-muted">{{$t('per_purchase')}}</small>
        </div>
        <div v-else class="text-center">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
    </div>
    
    <!-- Status Breakdown -->
    <div class="row mb-3">
      <InsightCard
        :visible="visibleCards.statusBreakdown"
        :title="$t('purchase_status_breakdown')"
        icon="pie-chart"
        container-class="col-12"
        @refresh="fetchInsights"
      >
        <div v-if="insights.status" class="row text-center">
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 border rounded">
              <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" style="font-size: 2rem;" />
              <h6 class="mt-2 mb-0">{{$t('received')}}</h6>
              <p class="mb-0 fw-bold text-success">{{ formatMoney(insights.status.received.amount) }}</p>
              <small class="text-muted">{{ insights.status.received.count }} {{$t('purchases')}}</small>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 border rounded">
              <AppIcon name="bi-clock" class="bi bi-clock text-warning" style="font-size: 2rem;" />
              <h6 class="mt-2 mb-0">{{$t('pending')}}</h6>
              <p class="mb-0 fw-bold text-warning">{{ formatMoney(insights.status.pending.amount) }}</p>
              <small class="text-muted">{{ insights.status.pending.count }} {{$t('purchases')}}</small>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 border rounded">
              <AppIcon name="bi-hourglass-split" class="bi bi-hourglass-split text-info" style="font-size: 2rem;" />
              <h6 class="mt-2 mb-0">{{$t('partially_received')}}</h6>
              <p class="mb-0 fw-bold text-info">{{ formatMoney(insights.status.partially_received.amount) }}</p>
              <small class="text-muted">{{ insights.status.partially_received.count }} {{$t('purchases')}}</small>
            </div>
          </div>
          <div class="col-md-3 col-6 mb-2">
            <div class="p-3 border rounded">
              <AppIcon name="bi-x-circle" class="bi bi-x-circle text-danger" style="font-size: 2rem;" />
              <h6 class="mt-2 mb-0">{{$t('canceled')}}</h6>
              <p class="mb-0 fw-bold text-danger">{{ formatMoney(insights.status.canceled.amount) }}</p>
              <small class="text-muted">{{ insights.status.canceled.count }} {{$t('purchases')}}</small>
            </div>
          </div>
        </div>
      </InsightCard>
    </div>
    
    <!-- Daily Purchases Chart -->
    <div class="row mb-3">
      <InsightCard
        :visible="visibleCards.dailyPurchases"
        :title="$t('daily_purchase_trend')"
        icon="graph-up"
        container-class="col-12"
        @refresh="fetchInsights"
      >
        <canvas id="dailyPurchasesChart" width="400" height="100"></canvas>
      </InsightCard>
    </div>
    
    <!-- Top Products & Top Suppliers -->
    <div class="row mb-3">
      <InsightCard
        :visible="visibleCards.topProducts"
        :title="$t('top_purchased_products')"
        icon="box-seam"
        container-class="col-lg-6"
        @refresh="fetchInsights"
      >
        <div v-if="insights.topProducts && insights.topProducts.length > 0">
          <ul class="list-group list-group-flush">
            <li v-for="(product, idx) in insights.topProducts.slice(0, 5)" :key="idx" 
                class="list-group-item d-flex justify-content-between align-items-center py-2">
              <div class="d-flex align-items-center">
                <span class="badge rounded-pill bg-secondary me-2" style="font-size: 0.7rem;">{{ idx + 1 }}</span>
                <span class="text-truncate" style="max-width:70%">{{ product.label }}</span>
              </div>
              <div class="text-end">
                <span class="badge rounded-pill bg-info">{{ product.quantity }} {{$t('units')}}</span>
                <div><small class="text-muted">{{ formatMoney(product.cost) }}</small></div>
              </div>
            </li>
          </ul>
        </div>
        <div v-else class="text-center py-3">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
      
      <InsightCard
        :visible="visibleCards.topSuppliers"
        :title="$t('top_suppliers')"
        icon="people-fill"
        container-class="col-lg-6"
        @refresh="fetchInsights"
      >
        <div v-if="insights.topSuppliers && insights.topSuppliers.length > 0">
          <ul class="list-group list-group-flush">
            <li v-for="(supplier, idx) in insights.topSuppliers.slice(0, 5)" :key="idx" 
                class="list-group-item d-flex justify-content-between align-items-center py-2">
              <div class="d-flex align-items-center">
                <span class="badge rounded-pill bg-secondary me-2" style="font-size: 0.7rem;">{{ idx + 1 }}</span>
                <span class="text-truncate" style="max-width:60%">{{ supplier.name }}</span>
              </div>
              <div class="text-end">
                <span class="fw-bold text-primary">{{ formatMoney(supplier.totalPurchased) }}</span>
                <div><small class="text-muted">{{ supplier.purchaseCount }} {{$t('purchases')}}</small></div>
              </div>
            </li>
          </ul>
        </div>
        <div v-else class="text-center py-3">
          <p class="mb-0 text-muted small">{{$t('no_data')}}</p>
        </div>
      </InsightCard>
    </div>
    
    <!-- Pending Purchases Table -->
    <div class="row">
      <InsightCard
        :visible="visibleCards.pendingPurchases"
        :title="$t('pending_purchases')"
        icon="file-earmark-text"
        container-class="col-12"
        @refresh="fetchInsights"
      >
        <div>
          <div class="table-responsive">
            <table class="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <sortable-table-header field="reference" label="reference" :sort-field="pendingPurchaseSortField" :sort-direction="pendingPurchaseSortDirection" header-class="" @sort="toggleSort($event, 'pendingPurchaseSortField', 'pendingPurchaseSortDirection')" />
                  <sortable-table-header field="supplier" label="supplier" :sort-field="pendingPurchaseSortField" :sort-direction="pendingPurchaseSortDirection" header-class="" @sort="toggleSort($event, 'pendingPurchaseSortField', 'pendingPurchaseSortDirection')" />
                  <sortable-table-header field="total" label="total" :sort-field="pendingPurchaseSortField" :sort-direction="pendingPurchaseSortDirection" header-class="text-end" @sort="toggleSort($event, 'pendingPurchaseSortField', 'pendingPurchaseSortDirection')" />
                  <sortable-table-header field="status" label="status" :sort-field="pendingPurchaseSortField" :sort-direction="pendingPurchaseSortDirection" header-class="" @sort="toggleSort($event, 'pendingPurchaseSortField', 'pendingPurchaseSortDirection')" />
                  <sortable-table-header field="date" label="date" :sort-field="pendingPurchaseSortField" :sort-direction="pendingPurchaseSortDirection" header-class="" @sort="toggleSort($event, 'pendingPurchaseSortField', 'pendingPurchaseSortDirection')" />
                </tr>
              </thead>
              <tbody>
                <tr v-for="purchase in sortedPendingPurchases" :key="purchase.id">
                  <td><strong>{{ purchase.reference }}</strong></td>
                  <td>{{ purchase.supplier }}</td>
                  <td class="text-end">{{ formatMoney(purchase.total) }}</td>
                  <td>
                    <span class="badge" :class="getStatusBadgeClass(purchase.status)">
                      {{ $t(purchase.status) }}
                    </span>
                  </td>
                  <td>{{ formatDate(purchase.date) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-if="!insights.pendingPurchases || !insights.pendingPurchases.purchases || !insights.pendingPurchases.purchases.length" class="text-center py-3">
          <p class="mb-0 text-muted small">{{$t('no_pending_purchases')}}</p>
        </div>
      </InsightCard>
    </div>
  </div>
</template>

<script>
import helper from '../../helper';
import DatePicker from 'vue2-datepicker';
import moment from 'moment';
import Chart from 'chart.js';
import InsightCard from '../../components/InsightCard.vue';
import 'vue2-datepicker/index.css';
import sortableList from '../../mixins/sortableList'

export default {
  name: 'PurchaseInsights',
  persistedFilterState: {
    key: 'purchase-insights',
    fields: ['dateRangeOption', 'range', 'start', 'end', 'pendingPurchaseSortField', 'pendingPurchaseSortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  components: { DatePicker, InsightCard },
  data() {
    return {
      range: [new Date(), new Date()],
      start: moment().format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD'),
      dateRangeOption: '',
      showSettings: false,
      pendingPurchaseSortField: '',
      pendingPurchaseSortDirection: 'asc',
      
      // Insights data from backend
      insights: {
        summary: null,
        status: null,
        pendingPurchases: null,
        topProducts: [],
        topSuppliers: [],
        dailyPurchases: []
      },
      
      // Chart instance
      dailyPurchasesChart: null,
      
      // Visibility controls
      visibleCards: {
        totalPurchases: true,
        totalCost: true,
        potentialProfit: true,
        avgPurchase: true,
        statusBreakdown: true,
        dailyPurchases: true,
        topProducts: true,
        topSuppliers: true,
        pendingPurchases: true
      }
    };
  },
  watch: {
    range() {
      if (this.range[0] == null) {
        this.start = null;
        this.end = null;
      }
      if (this.range.length && this.range[0] && this.range[1]) {
        this.start = moment(this.range[0]).format('YYYY-MM-DD');
        this.end = moment(this.range[1]).format('YYYY-MM-DD');
      } else {
        this.start = null;
        this.end = null;
      }
      this.fetchInsights();
    }
  },
  methods: {
    formatMoney(v) { 
      return helper.formatMoney(v); 
    },
    
    formatDate(d) {
      if (!d) return '';
      return moment(d).format('DD/MM/YYYY');
    },
    
    getStatusBadgeClass(status) {
      const statusMap = {
        'received': 'bg-success',
        'pending': 'bg-warning',
        'partially_received': 'bg-info',
        'canceled': 'bg-danger'
      };
      return statusMap[status?.toLowerCase()] || 'bg-secondary';
    },
    
    async fetchInsights() {
      try {
        let url = `${process.env.VUE_APP_API}/purchases/insights/overview`;
        if (this.start && this.end) {
          url += `?start=${this.start}&end=${this.end}`;
        }
        
        const res = await helper.customGet(url);
        this.insights = res?.data || {
          summary: null,
          status: null,
          pendingPurchases: null,
          topProducts: [],
          topSuppliers: [],
          dailyPurchases: []
        };
        
        // Render chart after data is loaded
        this.$nextTick(() => {
          this.renderDailyPurchasesChart();
        });
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message || this.$t('failed_fetch_insights')
        });
      }
    },
    
    renderDailyPurchasesChart() {
      this.$nextTick(() => {
        const el = document.getElementById('dailyPurchasesChart');
        if (!el) return;
        
        if (this.dailyPurchasesChart) {
          this.dailyPurchasesChart.destroy();
        }
        
        if (!this.insights.dailyPurchases || this.insights.dailyPurchases.length === 0) {
          return;
        }
        
        const labels = this.insights.dailyPurchases.map(x => moment(x.date).format('MMM DD'));
        const data = this.insights.dailyPurchases.map(x => x.amount);
        
        this.dailyPurchasesChart = new Chart(el, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: this.$t('purchase_amount'),
              data,
              borderColor: '#dc3545',
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              borderWidth: 3,
              pointBackgroundColor: '#fff',
              pointBorderColor: '#dc3545',
              pointBorderWidth: 2,
              pointRadius: 4,
              fill: true,
              tension: 0.3
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            tooltips: {
              callbacks: {
                label: (ti, d) => `${this.$t('amount')}: ${this.formatMoney(d.datasets[0].data[ti.index])}`
              }
            },
            scales: {
              yAxes: [{
                ticks: {
                  callback: (v) => this.formatMoney(v)
                }
              }]
            }
          }
        });
      });
    },
    
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        this.range = [];
        this.fetchInsights();
      } else if (this.dateRangeOption === 'today') {
        const start = new Date();
        const end = new Date();
        this.range = [start, end];
      } else if (this.dateRangeOption === 'yesterday') {
        const start = new Date();
        const end = new Date();
        start.setTime(start.getTime() - 3600 * 1000 * 24);
        end.setTime(end.getTime() - 3600 * 1000 * 24);
        this.range = [start, end];
      } else if (this.dateRangeOption === 'this_week') {
        const start = new Date();
        const end = new Date();
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
        this.range = [start, end];
      } else if (this.dateRangeOption === 'last_week') {
        const start = new Date();
        const end = new Date();
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 14);
        end.setTime(end.getTime() - 3600 * 1000 * 24 * 7);
        this.range = [start, end];
      } else if (this.dateRangeOption === 'this_month') {
        const start = new Date();
        const end = new Date();
        start.setDate(1);
        this.range = [start, end];
      } else if (this.dateRangeOption === 'custom') {
        this.range = [];
      }
    }
  },
  
  async mounted() {
    // Check permissions
    if (!helper.checkModuleIsEnabled('purchase_insights')) return this.$router.push({ path: '/' });
    if (helper.checkAccess('view_purchases') == -1) return this.$router.push({ path: '/' });
    if (helper.checkAccess('view_purchases') == -2) return this.$router.push({ path: '/subscribe' });

    // Bootstrap icons if not loaded
    if (!document.getElementById('bootstrap-icons')) {
      const link = document.createElement('link');
      link.id = 'bootstrap-icons';
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css';
      document.head.appendChild(link);
    }

    // Initialize with this_month
    this.dateRangeOption = 'this_month';
    // this.handleDateRangeChange();
    
    await this.fetchInsights();
  },
  
  beforeDestroy() {
    if (this.dailyPurchasesChart) {
      this.dailyPurchasesChart.destroy();
    }
  },
  computed: {
    sortedPendingPurchases() {
      return this.sortRows(this.insights.pendingPurchases?.purchases || [], this.pendingPurchaseSortField, this.pendingPurchaseSortDirection);
    }
  }
};
</script>

<style scoped>
.card { 
  transition: all 0.3s ease-in-out; 
  border-radius: 12px; 
  overflow: hidden; 
}
.card:hover { 
  transform: translateY(-4px); 
  box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important; 
}
#dailyPurchasesChart { 
  height: 300px; 
  min-height: 250px; 
}
</style>
